import React from 'react';
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';

import { ExpertAdviceFooterType, PostType } from 'libs/contentful/types';
import type { WRelatedLinkView, WModuleGroupView } from '@zola/svc-web-api-ts-client';

import { getCachedExpertAdviceFooterData, getCachedPostData } from 'libs/contentful/api';
import { fetchNavHTML } from 'libs/server/nav';
import { fetchSEORelatedLinks } from 'libs/server/seo';
import { fetchUser } from 'libs/server/users';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { fetchModuleGroups } from 'libs/server/moduleGroups';

import Nav from 'components/Nav';
import { Header } from 'components/expert-advice';
import ArticlePage from 'components/expert-advice/ArticlePage';

import { notEmpty, stripUndef } from 'libs/universal/utils/type';
import { badRequest } from 'libs/universal/errors';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';
import logger from '@zola-helpers/server/dist/es/logger';

import { TRACKING_SECTIONS_EA } from 'constants/tracking';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

// Dynamically imported components
const MarketingModuleSection = dynamic(import('components/expert-advice/MarketingModuleSection'));

export type PostProps = {
  expertAdviceFooter: ExpertAdviceFooterType;
  navHTML: string | null;
  userCtx: UserContext | null;
  page: PostType;
  marketingModuleMap?: Record<string, WModuleGroupView>;
  relatedLinks: WRelatedLinkView[];
};

const Post: React.FC<PostProps> = ({
  expertAdviceFooter,
  navHTML,
  userCtx,
  page,
  marketingModuleMap,
  relatedLinks = [],
}) => {
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const { topic, subtopic } = page;
  const breadcrumbs = [{ to: '/expert-advice', label: 'Expert advice' }];
  if (topic && topic.name) {
    breadcrumbs.push({ to: `/expert-advice/topic/${topic.slug}`, label: topic.name });
  }
  if (topic && subtopic && subtopic.name) {
    breadcrumbs.push({
      to: `/expert-advice/topic/${topic.slug}/${subtopic.slug}`,
      label: subtopic.name,
    });
  }

  const renderMarketingModuleSection = (i: 1 | 2 | 3 | 4) => {
    const widget = page[`widget${i}`];
    const widgetTag = widget?.marketingModule?.tag;
    const widgetBusinessUnit = widget?.marketingModule?.businessUnit;
    const widgetView = marketingModuleMap && widgetTag ? marketingModuleMap[widgetTag] : undefined;

    const trackingData = {
      business_unit: widgetBusinessUnit || '',
      section: TRACKING_SECTIONS_EA.EA_ARTICLE,
      position: i, // position is 1-indexed
    };

    return <MarketingModuleSection widget={widgetView} trackingData={trackingData} />;
  };

  return (
    <>
      <Header post={page} breadcrumbs={breadcrumbs} />
      <Nav
        activePrimaryLink={PrimaryNavIdentifier.ADVICE}
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disableSecondary
        disablePrimaryNavCollapse
      />
      <ArticlePage
        article={page}
        breadcrumbs={breadcrumbs}
        expertAdviceFooter={expertAdviceFooter}
        isPostAuthNav3={isPostAuthNav3Enabled}
        relatedLinks={relatedLinks}
        renderMarketingModuleSection={renderMarketingModuleSection}
        userCtx={userCtx}
      />
    </>
  );
};
export default Post;

// NOTE: keep this function for now. Maybe we will use static side generation in the near future
// export async function getStaticPaths() {
//   const allPosts = await getAllPostsWithSlug();
//   const paths = allPosts.map(({ slug }) => {
//     return { params: { slug } };
//   });
//   return {
//     paths: [{ params: { slug: 'in-laws-expectations-for-wedding' } }],
//     fallback: true,
//   };
// }

export const getServerSideProps: GetServerSideProps<PostProps> = async ({ params, req }) => {
  if (!params || !params.slug || Array.isArray(params.slug)) {
    throw badRequest('Missing or invalid slug');
  }
  const [expertAdviceFooter, navHTML, userCtx, postData] = await Promise.all([
    getCachedExpertAdviceFooterData(req),
    fetchNavHTML(req),
    fetchUser(req),
    // Fetch necessary data for the blog post using params.id
    getCachedPostData(params.slug, req),
  ]);

  if (!postData.page) {
    logger.error(`Page not found: ${params.slug}`);
    return { notFound: true };
  }

  const widgetTags = [
    postData.page.widget1?.marketingModule?.tag,
    postData.page.widget2?.marketingModule?.tag,
    postData.page.widget3?.marketingModule?.tag,
    postData.page.widget4?.marketingModule?.tag,
  ];
  const moduleGroupTags = widgetTags.filter(notEmpty);
  const marketingModuleMap = moduleGroupTags.length
    ? await fetchModuleGroups(req, moduleGroupTags)
    : undefined;

  const topicSlug = postData.page.topic?.slug;
  const subtopicSlug = postData.page.subtopic?.slug;
  const relatedLinks =
    topicSlug && subtopicSlug ? await fetchSEORelatedLinks({ topicSlug, subtopicSlug }) : [];

  return {
    props: stripUndef<PostProps>({
      expertAdviceFooter,
      navHTML,
      userCtx,
      page: postData.page,
      marketingModuleMap,
      relatedLinks,
    }),
  };
};
